import { Box, DialogContent, makeStyles, Typography } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogActions from "@material-ui/core/DialogActions";
import { withStyles } from "@material-ui/core/styles";
import React from "react";

import PropTypes from "prop-types";
import { glassBlur, glassColors, glassAnimations } from "../../style/glass";

const DialogActions = withStyles(() => ({
  root: {
    margin: 0,
    padding: "16px 24px",
    gap: 12,
    justifyContent: "flex-end",
  },
}))(MuiDialogActions);

const useStyles = makeStyles(() => ({
  dialog: {
    "& .MuiDialog-paper": {
      background: glassColors.glass.thick,
      ...glassBlur.heavy,
      borderRadius: 16,
      border: "0.5px solid rgba(255, 255, 255, 0.25)",
      boxShadow:
        "0 24px 48px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)",
      minWidth: 340,
      maxWidth: 420,
      overflow: "hidden",
    },
  },
  backdrop: {
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "20px 24px 0 24px",
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "rgba(255, 59, 48, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  icon: {
    width: 20,
    height: 20,
    color: glassColors.system.red,
  },
  titleStyle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: glassColors.label.primary,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
  },
  descStyle: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.6,
    color: glassColors.label.secondary,
    padding: "12px 24px 8px 24px",
  },
  actions: {
    borderTop: `1px solid ${glassColors.separator.nonOpaque}`,
  },
  cancelBtn: {
    padding: "10px 20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: glassColors.label.primary,
    background: glassColors.fill.tertiary,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: glassAnimations.snappy,
    "&:hover": {
      background: glassColors.fill.secondary,
    },
  },
  confirmBtn: {
    padding: "10px 20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#FFFFFF",
    background: "#2563eb",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: glassAnimations.snappy,
    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
    "&:hover": {
      background: "#1d4ed8",
      boxShadow: "0 4px 12px rgba(37, 99, 235, 0.35)",
    },
    "&:disabled": {
      background: glassColors.fill.secondary,
      color: glassColors.label.tertiary,
      boxShadow: "none",
      cursor: "not-allowed",
    },
  },
  confirmBtnDanger: {
    background: glassColors.system.red,
    boxShadow: "0 2px 8px rgba(255, 59, 48, 0.25)",
    "&:hover": {
      background: "#e53935",
      boxShadow: "0 4px 12px rgba(255, 59, 48, 0.35)",
    },
  },
}));

export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title = "Confirmation",
  description = "Are you sure?",
  btnName = "Confirm",
  disabled = false,
  variant = "default", // "default" | "danger"
  customContent = null,
  ...props
}) {
  const classes = useStyles();
  const isDanger =
    variant === "danger" ||
    btnName.toLowerCase() === "dismiss" ||
    btnName.toLowerCase() === "delete";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      BackdropProps={{ className: classes.backdrop }}
      disableEnforceFocus
      style={{ zIndex: 1500 }}
    >
      <Box className={classes.header}>
        <Box className={classes.iconWrapper}>
          <svg className={classes.icon} viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
        <Typography className={classes.titleStyle}>{title}</Typography>
      </Box>
      <DialogContent className={classes.descStyle}>
        {description}

        {customContent ? <Box mt={1}>{customContent}</Box> : null}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Box component="button" className={classes.cancelBtn} onClick={onClose}>
          Cancel
        </Box>
        <Box
          component="button"
          className={`${classes.confirmBtn} ${isDanger ? classes.confirmBtnDanger : ""}`}
          onClick={onConfirm}
          disabled={disabled}
        >
          {btnName}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ConfirmModal.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
};
